// src/components/Visualization/DataWidget.js
import React, { useState } from 'react';
import { 
  BarChart2, 
  PieChart as PieChartIcon, 
  TrendingUp, 
  List, 
  Download, 
  ExternalLink, 
  Maximize2, 
  Minimize2,
  Loader,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

// Importar componentes de visualização
import MetricChart from './MetricChart';
import PerformanceBar from './PerformanceBar';
import DistributionPie from './DistributionPie';
import TrendLine from './TrendLine';
import MetricTable from './MetricTable';
import ScatterAnalysis from './ScatterAnalysis';
import ComparisonChart from './ComparisonChart';
import FunnelChart from './FunnelChart';
import InsightGenerator from './InsightGenerator';

/**
 * DataWidget - Container inteligente para visualizações de dados
 * 
 * Um componente flexível que pode exibir diferentes tipos de visualizações
 * com controles e estados unificados.
 * 
 * @param {Object} props
 * @param {string} props.title - Título do widget
 * @param {string} props.type - Tipo de visualização ('chart', 'bar', 'pie', 'table', 'trend', 'scatter', 'funnel', 'comparison', 'insights')
 * @param {Object} props.config - Configuração específica para o tipo de visualização
 * @param {Array} props.data - Dados para a visualização
 * @param {boolean} props.loading - Estado de carregamento
 * @param {string} props.error - Mensagem de erro, se houver
 * @param {Array} props.viewOptions - Opções de visualização disponíveis
 * @param {boolean} props.expandable - Se o widget pode ser expandido
 * @param {boolean} props.showControls - Exibir controles (download, atualizar, etc.)
 * @param {Function} props.onRefresh - Função para atualizar dados
 * @param {Function} props.onDownload - Função para baixar dados
 * @param {Function} props.onConfigChange - Callback quando a configuração mudar
 * @param {string} props.className - Classes adicionais para o container
 * @param {number} props.height - Altura do widget
 */
const DataWidget = ({
  title = 'Visualização de Dados',
  type = 'chart',
  config = {},
  data = [],
  loading = false,
  error = null,
  viewOptions = [],
  expandable = true,
  showControls = true,
  onRefresh = null,
  onDownload = null,
  onConfigChange = null,
  className = '',
  height = 400
}) => {
  // Estados
  const [activeView, setActiveView] = useState(type);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  
  // Determinar qual visualização renderizar com base no tipo ativo
  const renderVisualization = () => {
    // Se estiver carregando, mostrar indicador
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full min-h-[200px]">
          <Loader className="h-8 w-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-500">Carregando dados...</span>
        </div>
      );
    }
    
    // Se houver erro, mostrar mensagem
    if (error) {
      return (
        <div className="flex items-center justify-center h-full min-h-[200px] bg-red-50 rounded-lg p-4">
          <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
          <span className="text-red-600">{error}</span>
        </div>
      );
    }
    
    // Se não houver dados, mostrar mensagem
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[200px] bg-gray-50 rounded-lg p-4">
          <BarChart2 className="h-10 w-10 text-gray-400 mb-2" />
          <p className="text-gray-500 text-center max-w-xs">
            Nenhum dado disponível para visualização.
            {onRefresh && (
              <button 
                className="text-blue-600 hover:text-blue-800 ml-1 underline"
                onClick={onRefresh}
              >
                Atualizar
              </button>
            )}
          </p>
        </div>
      );
    }
    
    // Renderizar a visualização apropriada com base no tipo ativo
    switch (activeView) {
      case 'chart':
        return <MetricChart {...config} data={data} height={height} />;
      
      case 'bar':
        return <PerformanceBar {...config} data={data} height={height} />;
      
      case 'pie':
        return (
          <div style={{ height, width: '100%' }}>
            <DistributionPie {...config} data={data} />
          </div>
        );
      
      case 'trend':
        return (
          <div style={{ height, width: '100%' }}>
            <TrendLine {...config} data={data} />
          </div>
        );
      
      case 'table':
        return <MetricTable {...config} data={data} />;
      
      case 'scatter':
        return <ScatterAnalysis {...config} data={data} height={height} />;
      
      case 'comparison':
        return <ComparisonChart {...config} data={data} height={height} />;
      
      case 'funnel':
        return <FunnelChart {...config} data={data} height={height} />;
      
      case 'insights':
        return <InsightGenerator {...config} data={data} />;
      
      default:
        return (
          <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg p-4">
            <p className="text-gray-500">Tipo de visualização não suportado: {activeView}</p>
          </div>
        );
    }
  };
  
  // Obter ícone para tipo de visualização
  const getViewIcon = (viewType) => {
    switch (viewType) {
      case 'chart':
        return <BarChart2 size={18} />;
      case 'bar':
        return <BarChart2 size={18} />;
      case 'pie':
        return <PieChartIcon size={18} />;
      case 'trend':
        return <TrendingUp size={18} />;
      case 'table':
        return <List size={18} />;
      case 'scatter':
        return <BarChart2 size={18} />;
      case 'comparison':
        return <BarChart2 size={18} />;
      case 'funnel':
        return <BarChart2 size={18} />;
      case 'insights':
        return <BarChart2 size={18} />;
      default:
        return <BarChart2 size={18} />;
    }
  };
  
  // Renderizar cabeçalho do widget
  const renderHeader = () => {
    if (!title && !showControls && viewOptions.length === 0) return null;
    
    return (
      <div className="flex flex-wrap items-center justify-between p-4 border-b border-gray-200">
        <h3 className="font-medium text-gray-800 flex items-center">
          {getViewIcon(activeView)}
          <span className="ml-2">{title}</span>
        </h3>
        
        <div className="flex items-center space-x-2">
          {/* Opções de visualização */}
          {viewOptions.length > 0 && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              {viewOptions.map((option, index) => (
                <button
                  key={`view-${index}`}
                  className={`p-1.5 rounded-md ${
                    activeView === option.value
                      ? 'bg-white shadow-sm text-blue-600'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  onClick={() => {
                    setActiveView(option.value);
                    if (onConfigChange) {
                      onConfigChange({ type: option.value });
                    }
                  }}
                  title={option.label}
                >
                  {getViewIcon(option.value)}
                </button>
              ))}
            </div>
          )}
          
          {/* Controles adicionais */}
          {showControls && (
            <div className="flex items-center space-x-2">
              {/* Botão de atualizar */}
              {onRefresh && (
                <button
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                  onClick={onRefresh}
                  title="Atualizar dados"
                >
                  <RefreshCw size={18} />
                </button>
              )}
              
              {/* Botão de download */}
              {onDownload && (
                <button
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                  onClick={onDownload}
                  title="Baixar dados"
                >
                  <Download size={18} />
                </button>
              )}
              
              {/* Botão de abrir em nova janela */}
              <button
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                title="Abrir em nova janela"
              >
                <ExternalLink size={18} />
              </button>
              
              {/* Botão de expandir/colapsar */}
              {expandable && (
                <button
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                  onClick={() => setIsExpanded(!isExpanded)}
                  title={isExpanded ? "Restaurar" : "Expandir"}
                >
                  {isExpanded ? (
                    <Minimize2 size={18} />
                  ) : (
                    <Maximize2 size={18} />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Classes para o container, considerando estado expandido
  const containerClasses = `
    bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden
    ${isExpanded ? 'fixed inset-4 z-50 flex flex-col' : ''}
    ${className}
  `;
  
  return (
    <div className={containerClasses}>
      {renderHeader()}
      
      <div className={`${isExpanded ? 'flex-1 overflow-auto' : ''}`}>
        {renderVisualization()}
      </div>
      
      {/* Overlay de fundo quando expandido */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40"
          onClick={() => setIsExpanded(false)}
        ></div>
      )}
    </div>
  );
};

export default DataWidget;
